// Formatting and status helpers shared by the admin People views

import type { AdminCoupleSummary, AdminReviewerSummary } from '@/types';

export type ActivityState = 'online' | 'active' | 'idle' | 'dormant';

const ONLINE_MS = 2 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

// ---- Time helpers ----

export const ms = (iso?: string) => (iso ? new Date(iso).getTime() || 0 : 0);

export function timeAgo(iso?: string): string {
  const t = ms(iso);
  if (!t) return '—';
  const diff = Date.now() - t;
  if (diff < 60 * 1000) return 'just now';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < DAY_MS) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 30 * DAY_MS) return `${Math.floor(diff / DAY_MS)}d ago`;
  return shortDate(iso);
}

export function shortDate(iso?: string): string {
  const t = ms(iso);
  if (!t) return '—';
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
}

export function deviceLabel(ua?: string): string | undefined {
  if (!ua) return undefined;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return undefined;
}

export type Person = AdminCoupleSummary | AdminReviewerSummary;

// Latest sign of life: presence heartbeat, session start, or (couples) an in-app action
export function lastActiveAt(p: Person): string | undefined {
  const candidates = [p.lastSeen, p.lastLoginAt, 'lastActivityAt' in p ? p.lastActivityAt : undefined];
  return candidates.reduce<string | undefined>((best, c) => (ms(c) > ms(best) ? c : best), undefined);
}

export function activityState(p: Person): ActivityState {
  if (Date.now() - ms(p.lastSeen) < ONLINE_MS) return 'online';
  const age = Date.now() - ms(lastActiveAt(p));
  if (age < 7 * DAY_MS) return 'active';
  if (age < 30 * DAY_MS) return 'idle';
  return 'dormant';
}

export const coupleAttention = (c: AdminCoupleSummary) => c.pendingSubmissionCount + c.unreadFromCoupleCount + c.pendingRequestCount;
export const reviewerPending = (r: AdminReviewerSummary) => r.pendingFollowUpCount + r.pendingEditCount + r.unreadTutorialCount;
export const coupleName = (c: AdminCoupleSummary) => [c.hisName, c.herName].filter(Boolean).join(' & ') || c.coupleId;
